import React, { useEffect, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import { useReveal } from '@/lib/motion';

// News widget — homepage, immediately above FAQ.
//
// Layout mirrors Sol's reference screenshot (Sports Business Journal /
// msn.com editorial style): one large featured story on the left, two
// stacked secondary stories in the middle, and a "TOP HEADLINES"
// title-only list on the right. Placement is derived purely from admin
// `order`: item 1 is featured, items 2-3 are secondary, everything after
// is a headline row. Every block is a plain outbound link — clicking goes
// DIRECTLY to the article (transcript 12:50-13:03); no flip, no popup.
//
// The section renders NOTHING until at least one visible article exists,
// so shipping it changes nothing visitors see. FAQ below is untouched.

const HEADLINE_LIST_CAP = 8;

function TagChip({ tag }) {
  if (!tag || !String(tag).trim()) return null;
  return (
    <span
      className="inline-block bg-brand-red text-white font-body font-bold uppercase text-[10px] tracking-wider2 px-2 py-1 rounded-sm"
      data-testid="news-tag"
    >
      {tag}
    </span>
  );
}

function FeaturedCard({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={item.title}
      className="group relative block overflow-hidden rounded-sm bg-card-dark self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
      data-testid="news-featured"
    >
      <div className="relative" style={{ aspectRatio: '4/3' }}>
        {item.image ? (
          <img
            loading="lazy"
            decoding="async"
            src={fileUrl(item.image) || item.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0 bg-card-dark" aria-hidden="true" />
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent pt-16 pb-5 px-5">
          <TagChip tag={item.tag} />
          <h3 className="mt-2 font-display font-bold text-white leading-[1.15] text-[22px] md:text-[26px]" data-testid="news-featured-title">
            {item.title}
          </h3>
          {item.summary && String(item.summary).trim() && (
            <p className="mt-2 font-body text-[13px] md:text-[14px] leading-[1.6] text-white/85 line-clamp-3" data-testid="news-featured-summary">
              {item.summary}
            </p>
          )}
        </div>
      </div>
    </a>
  );
}

function SecondaryCard({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={item.title}
      className="group relative block overflow-hidden rounded-sm bg-card-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
      data-testid="news-secondary"
    >
      <div className="relative" style={{ aspectRatio: '16/9' }}>
        {item.image ? (
          <img
            loading="lazy"
            decoding="async"
            src={fileUrl(item.image) || item.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0 bg-card-dark" aria-hidden="true" />
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent pt-12 pb-4 px-4">
          <TagChip tag={item.tag} />
          <h3 className="mt-2 font-display font-bold text-white leading-[1.15] text-[16px] md:text-[18px]" data-testid="news-secondary-title">
            {item.title}
          </h3>
        </div>
      </div>
    </a>
  );
}

function HeadlineList({ items }) {
  return (
    <div className="self-start" data-testid="news-headlines">
      <p className="font-display font-bold uppercase text-brand-red text-[13px] tracking-wider2">
        Top Headlines
      </p>
      <ul className="mt-1">
        {items.map((it) => (
          <li key={it.id} className="border-b border-ink-primary/15">
            <a
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block py-3.5 font-display font-semibold text-ink-primary text-[14px] md:text-[15px] leading-snug hover:text-brand-orange focus:outline-none focus-visible:text-brand-orange transition-colors"
              data-testid="news-headline-row"
            >
              {it.title}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VideoEmbed({ videoId, format = 'standard', className = '', title = 'Featured video' }) {
  // 'short' renders the vertical phone shape (9:16); anything else is
  // the original widescreen 16:9. The format was validated server-side
  // and re-checked against a strict allowlist before reaching here.
  const isShort = format === 'short';
  return (
    <div
      className={`relative rounded-sm overflow-hidden bg-card-dark ${className}`}
      style={{ aspectRatio: isShort ? '9/16' : '16/9' }}
      data-testid="news-video"
      data-video-format={isShort ? 'short' : 'standard'}
    >
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}`}
        title={title}
        loading="lazy"
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

// The video shelf: 1-3 videos. The server guarantees a standard
// (widescreen) video only ever appears ALONE; defensively, if one is
// present it renders alone here too. Shorts: one centered, two side by
// side, three equal columns on desktop — all stacking vertically on
// mobile, each capped at phone width.
function VideoShelf({ videos, standardClassName }) {
  const standard = videos.find((v) => v.format !== 'short');
  if (standard) {
    return <VideoEmbed videoId={standard.id} format="standard" className={standardClassName} />;
  }
  if (videos.length === 1) {
    return <VideoEmbed videoId={videos[0].id} format="short" className="w-full max-w-[400px] mx-auto" />;
  }
  const cols = videos.length === 2 ? 'md:grid-cols-2 max-w-[840px]' : 'md:grid-cols-3 max-w-[1280px]';
  return (
    <div className={`grid grid-cols-1 gap-5 mx-auto ${cols}`} data-testid="news-video-shelf">
      {videos.map((v, i) => (
        <VideoEmbed
          key={v.id}
          videoId={v.id}
          format="short"
          className="w-full max-w-[400px] mx-auto"
          // Multiple players need DISTINCT accessible names.
          title={`Featured video ${i + 1} of ${videos.length}`}
        />
      ))}
    </div>
  );
}

export default function NewsSection() {
  // Self-fetching, prop-free — same pattern as FAQSection, so Home.js
  // stays a two-line change and /public/home is untouched.
  // items: null until /news settles (failure settles to []).
  // videos: null until /public/news-videos settles; then a VALIDATED
  // array (failure settles to []). The whole shelf arrives in one
  // response, so settling this state settles the complete shelf.
  const [items, setItems] = useState(null);
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    let alive = true;
    // A fresh mount is un-settled — clear any flag left by a previous
    // visit so hash-landing waiters never act on stale settlement.
    window.__seiconNewsSettled = false;
    api.get('/news')
      .then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setItems([]); });
    api.get('/public/news-videos')
      .then((r) => {
        if (!alive) return;
        // Strict client-side re-validation: only 11-char YouTube id
        // shapes may ever reach an iframe src, only the literal
        // 'short' selects the tall player (anything unknown falls back
        // to 16:9, never to tall), and never more than three entries.
        const raw = Array.isArray(r.data?.videos) ? r.data.videos : [];
        const clean = [];
        for (const entry of raw) {
          const v = entry?.video_id;
          if (typeof v === 'string' && /^[A-Za-z0-9_-]{11}$/.test(v) && clean.length < 3) {
            clean.push({ id: v, format: entry?.display_format === 'short' ? 'short' : 'standard' });
          }
        }
        setVideos(clean);
      })
      .catch(() => { if (alive) setVideos([]); });
    return () => { alive = false; };
  }, []);

  // Settlement signal for hash landing: once BOTH requests have settled
  // (the videos response carries the COMPLETE shelf), this section's
  // final size is known, so scrolling to a section below it (e.g.
  // #faq) can no longer be displaced by late insertion.
  useEffect(() => {
    if (items !== null && videos !== null) {
      window.__seiconNewsSettled = true;
      window.dispatchEvent(new Event('seicon:news-settled'));
    }
  }, [items, videos]);

  // The reveal hooks live in NewsContent, NOT here. useReveal installs its
  // IntersectionObserver in a mount-time effect; if this component held the
  // hooks and returned null first, that effect would run with ref.current
  // === null, bail, and never rerun — the section would stay at opacity:0
  // forever once content mounted. SpeakerDetail documents this exact bug.
  // Mounting the content only after data exists means its hooks run on
  // their first render, when the DOM nodes are really there.
  // Rendering is DECOUPLED from the pending requests: loaded articles
  // never wait on the optional video request, and a loaded video never
  // waits on a slow /news. Only the settlement SIGNAL (above) requires
  // both, because it protects #faq hash positioning below this section.
  const hasArticles = items !== null && items.length > 0;
  const hasVideos = videos !== null && videos.length > 0;
  if (hasArticles) {
    // Articles render immediately; the shelf slots in beneath the grid
    // whenever its request settles.
    return <NewsContent items={items} videos={hasVideos ? videos : []} />;
  }
  if (hasVideos) {
    // Videos with zero articles, a failed /news, or /news still
    // pending: render ONLY the shelf — no empty News heading, no grid.
    return (
      <section id="news" className="section-pad relative bg-bg-canvas" data-testid="section-news">
        <div className="container-x">
          <VideoShelf videos={videos} standardClassName="max-w-[880px] mx-auto" />
        </div>
      </section>
    );
  }
  // Neither settled-with-content: nothing renders — the homepage looks
  // exactly as it did before this section existed.
  return null;
}

function NewsContent({ items, videos = [] }) {
  const headerReveal = useReveal();
  const gridReveal = useReveal();

  const featured = items[0];
  const secondary = items.slice(1, 3);
  const headlines = items.slice(3, 3 + HEADLINE_LIST_CAP);

  // Grid template adapts to how many zones actually have content, so one
  // or two articles never leave an empty column behind.
  const zones = 1 + (secondary.length > 0 ? 1 : 0) + (headlines.length > 0 ? 1 : 0);
  const gridCols =
    zones === 3 ? 'lg:grid-cols-[1.5fr_1.1fr_0.9fr]' : zones === 2 ? 'lg:grid-cols-[1.5fr_1.1fr]' : 'lg:grid-cols-1 lg:max-w-[720px] lg:mx-auto';

  return (
    <section id="news" className="section-pad relative bg-bg-canvas" data-testid="section-news">
      <div className="container-x">
        <div
          ref={headerReveal.ref}
          className={`text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}
        >
          <p className="eyebrow eyebrow-lg" data-testid="news-eyebrow">IN THE NEWS.</p>
          <h2
            className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]"
            data-testid="news-headline"
          >
            LATEST HEADLINES.
          </h2>
        </div>

        <div
          ref={gridReveal.ref}
          className={`mt-12 md:mt-14 grid grid-cols-1 gap-6 ${gridCols} ${gridReveal.simple ? 'reveal-simple' : 'reveal-up'} ${gridReveal.revealed ? 'is-in' : ''}`}
          data-testid="news-grid"
        >
          <FeaturedCard item={featured} />
          {secondary.length > 0 && (
            <div className="grid grid-cols-1 gap-6 content-start">
              {secondary.map((it) => (
                <SecondaryCard key={it.id} item={it} />
              ))}
            </div>
          )}
          {headlines.length > 0 && <HeadlineList items={headlines} />}
        </div>

        {/* Optional YouTube video shelf, per Sol: no title, responsive,
            and completely absent when no videos are configured. The
            privacy-enhanced host serves the embeds; ids and formats
            were validated server-side and again above. Shorts render
            phone-shaped (9:16) — one centered, two or three side by
            side on desktop, stacked on mobile; a standard video keeps
            the full-width 16:9 frame and only ever appears alone. */}
        {videos.length > 0 && (
          <div className="mt-8">
            <VideoShelf videos={videos} standardClassName="" />
          </div>
        )}
      </div>
    </section>
  );
}
